import { FormElementHelper } from '@/public/shared/helpers/form-element-helper';
import { ApiClient } from '@/shared/api-client/api-client';
import { IHttpValidationError } from '@/shared/api-client/models/http-validation-error';
import { INewElectionSignUp } from '@/shared/api-client/models/new-election-signup';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { ComplexIdHelper } from '@/shared/helpers/complex-id-helper';
import { ElectionSignUpFieldType } from '@/shared/Models/election-signup-field-type';
import _ from 'lodash';
import { Component, Prop, Vue } from 'vue-property-decorator';

interface IValidationErrorViewModel {

    formControlId?: string;

    name: string;

    message: string;
}

interface IViewModel {

    errors: IValidationErrorViewModel[];

    isStoring: boolean;

    isStored: boolean;
}

interface IFormDataModel {

    firstName: string;

    lastName: string;

    email: string;

    telephoneNumber: string;

    confirmConsent: boolean;
}

@Component({
    components: {

    }
})
export default class ElectionSignUpComponent extends Vue {

    @Prop() public electionId!: string;
    @Prop({default: null}) public authenticatedUserId?: string;

    @Prop() public confirmationOfConsent!: boolean;
    @Prop() public enabledFields!: ElectionSignUpFieldType[];
    @Prop() public requiredFields!: ElectionSignUpFieldType[];
    @Prop() public translations!: { [key: string]: string; };

    private apiClient: ApiClient = new ApiClient();

    private validateOnChanges = false;

    public formData: IFormDataModel = {
        firstName: '',
        lastName: '',
        email: '',
        telephoneNumber: '',
        confirmConsent: false
    };

    public viewModel: IViewModel = {
        errors: [],
        isStoring: false,
        isStored: false
    }

    public get isAuthenticated(): boolean {
        return !!this.authenticatedUserId;
    }

    public get enableConfirmationOfConsent(): boolean {
        return this.confirmationOfConsent;
    }

    public get formControlsWithErrors(): string[] {

        if (!this.viewModel.errors || this.viewModel.errors.length <= 0) {
            return [];
        }

        const errorFields = this.viewModel.errors.map(x => x.formControlId || 'noField');

        return _.uniq(errorFields);
    }

    public isEnabled(field: ElectionSignUpFieldType): boolean {

        ArgumentValidator.notNullOrEmpty(field, `field`);

        return this.enabledFields.includes(field);
    }

    public isRequired(field: ElectionSignUpFieldType): boolean {

        ArgumentValidator.notNullOrEmpty(field, `field`);

        return this.requiredFields.includes(field);
    }

    public async submit($event: UIEvent) {

        $event.preventDefault();

        if (this.viewModel.isStoring) {

            return;
        }

        this.validate(true);

        if (this.viewModel.errors !== null && this.viewModel.errors.length >= 1) {
            return;
        }

        this.viewModel.isStoring = true;

        try {

            const postModel: INewElectionSignUp = {
                firstName: this.formData.firstName,
                lastName: this.formData.lastName,
                telephoneNumber: this.formData.telephoneNumber,
                email: this.formData.email
            };

            if (this.isAuthenticated) {
                postModel.authenticatedUserId = this.authenticatedUserId;
            }

            await this.apiClient.election.signUp(this.electionId, postModel);

            this.viewModel.isStoring = false;

            this.viewModel.isStored = true;

        } catch (error: any) {

                const typedError = error as IHttpValidationError;

                this.viewModel.isStoring = false;

                if (typedError.type === 'ValidationException') {

                    const validationErrors = typedError.errors;

                    Object.keys(validationErrors).forEach(key => {

                        const messages = validationErrors[key] as string[];

                        messages.forEach( (message) => {

                            const camelCasedMessage = _.camelCase(message);

                            this.viewModel.errors.push({
                                formControlId: key,
                                name: this.translations[`form_${key}_header`] || '',
                                message: this.translations[`validation_error_${camelCasedMessage}`] || '',
                            });
                        });
                    });

                    return;

                } else if (typedError.type === 'InvalidModelException') {

                    // Generic api validation error
                    this.viewModel.errors.push({
                        name: this.translations[`validation_error_api_general_header`] || '',
                        message: this.translations[`validation_error_api_general_message`] || ''
                    });

                    return;
                }

                // Generic error
                this.viewModel.errors.push({
                    name: this.translations[`validation_error_general_header`] || '',
                    message: this.translations[`validation_error_general_message`] || ''
                });
            }

    }

    private validate(forceValidation?: boolean): void {

        if (forceValidation === false && this.validateOnChanges === false) {

            return;
        }

        if (this.isAuthenticated) {
            // No need to validate if user is authenticated
            return;
        }

        let isValid = true;
        this.viewModel.errors = [];

        if (this.isEnabled('FirstName')) {

            if (this.isRequired('FirstName') && !this.formData.firstName) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'firstName',
                    name: this.translations[`form_firstName_header`],
                    message: this.translations[`validation_error_generic_required`]
                });
            }
        }

        if (this.isEnabled('LastName')) {

            if (this.isRequired('LastName') && !this.formData.lastName) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'lastName',
                    name: this.translations[`form_lastName_header`],
                    message: this.translations[`validation_error_generic_required`]
                });
            }
        }

        if (this.isEnabled('Telephone')) {

            if (this.isRequired('Telephone') && !this.formData.telephoneNumber) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'telephoneNumber',
                    name: this.translations[`form_telephoneNumber_header`],
                    message: this.translations[`validation_error_generic_required`]
                });
            }

            if (!FormElementHelper.validateTelephoneNumber(this.formData.telephoneNumber)) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'telephoneNumber',
                    name: this.translations[`form_telephoneNumber_header`],
                    message: this.translations[`validation_error_telephoneNumber_inValid`]
                });
            }
        }

        if (this.isEnabled('Email')) {

            if (this.isRequired('Email') && !this.formData.email) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'email',
                    name: this.translations[`form_email_header`],
                    message: this.translations[`validation_error_generic_required`]
                });
            }

            if (!FormElementHelper.validateEmail(this.formData.email)) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'email',
                    name: this.translations[`form_email_header`],
                    message: this.translations[`validation_error_email_inValid`]
                });
            }
        }

        if (!this.validateAskedForConsent(this.formData.confirmConsent)) {

            isValid = false;

            this.viewModel.errors.push({

                formControlId: 'confirmConsent',
                name: this.translations[`form_confirmConsent_label`],
                message: this.translations[`validation_error_needConsent`]
            });
        }

        if (!isValid && this.validateOnChanges === false) {

            this.validateOnChanges = true;
        }
    }

    private validateAskedForConsent(askedForConsent: boolean): boolean {

        if (this.confirmationOfConsent === false) {

            // No validation
            return true;
        }

        if (askedForConsent === null) {

            return false;
        }

        return askedForConsent;
    }

    public handleTelephoneNumberChanged($event: UIEvent): void {

        this.formData.telephoneNumber = FormElementHelper.tryParseTelephoneNumber(this.formData.telephoneNumber);

        this.validate(false);
    }

}